import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { Button } from 'react-bootstrap';
import Flex from '../Flex';
import { useAdvanceTableContext } from '../../../providers/AdvanceTableProvider';

/**
 * 生成页码序列（0-based），最多露出 maxVisible 个页码，其余用省略号。
 * 例：共 40 页、当前第 4 页 → [0, -1, 2, 3, 4, -1, 39] → 1 … 3 4 5 … 40
 * @returns {(number)[]} 页索引；-1 表示省略号
 */
export function getPageNumbers(current, total, maxVisible = 5) {
	const pageCount = Number.isFinite(total) ? Math.max(0, Math.floor(total)) : 0;
	if (pageCount <= 0) return [];

	const safeCurrent = Math.min(Math.max(0, Math.floor(Number(current) || 0)), pageCount - 1);
	const visible = Math.max(1, Math.floor(Number(maxVisible) || 5));

	if (pageCount <= visible) {
		return Array.from({ length: pageCount }, (_, i) => i);
	}

	const include = new Set([0, pageCount - 1]);
	const interiorSlots = Math.max(1, visible - 2);
	let start = Math.max(1, safeCurrent - Math.floor((interiorSlots - 1) / 2));
	let end = start + interiorSlots - 1;
	if (end > pageCount - 2) {
		end = pageCount - 2;
		start = Math.max(1, end - interiorSlots + 1);
	}
	for (let i = start; i <= end; i += 1) include.add(i);

	const sorted = [...include].sort((a, b) => a - b);
	const pages = [];
	for (let i = 0; i < sorted.length; i += 1) {
		if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
			pages.push(-1);
		}
		pages.push(sorted[i]);
	}
	return pages;
}

/** Falcon 页码分页：上一页 / 1 … 3 4 5 … 40 / 下一页 */
export const AdvanceTablePagination = ({ disabled = false }) => {
	const { previousPage, nextPage, getCanNextPage, getCanPreviousPage, getState, getPageCount, setPageIndex } = useAdvanceTableContext();

	const {
		pagination: { pageIndex },
	} = getState();
	const rawPageCount = getPageCount();
	const totalPages = Number.isFinite(rawPageCount) && rawPageCount > 0 ? rawPageCount : 1;
	const pageNumbers = getPageNumbers(pageIndex, totalPages);
	const canPrev = getCanPreviousPage() && !disabled;
	const canNext = getCanNextPage() && !disabled;

	return (
		<Flex alignItems='center' justifyContent='center' className='advance-table-pagination'>
			<Button size='sm' variant='falcon-default' onClick={() => previousPage()} className={classNames({ disabled: !canPrev })} disabled={!canPrev}>
				<FontAwesomeIcon icon='chevron-left' />
			</Button>
			<ul className='pagination mb-0 mx-2 advance-table-pagination__pages'>
				{pageNumbers.map((page, idx) =>
					page === -1 ? (
						<li key={`ellipsis-${idx}`} className='disabled'>
							<span className='px-2'>…</span>
						</li>
					) : (
						<li key={page} className={classNames({ active: pageIndex === page })}>
							<Button
								size='sm'
								variant='falcon-default'
								className={classNames('page', {
									'me-2': idx + 1 !== pageNumbers.length,
								})}
								disabled={disabled}
								onClick={() => setPageIndex(page)}
							>
								{page + 1}
							</Button>
						</li>
					),
				)}
			</ul>
			<Button size='sm' variant='falcon-default' onClick={() => nextPage()} className={classNames({ disabled: !canNext })} disabled={!canNext}>
				<FontAwesomeIcon icon='chevron-right' />
			</Button>
		</Flex>
	);
};

AdvanceTablePagination.propTypes = {
	disabled: PropTypes.bool,
};

export default AdvanceTablePagination;
